package gr.tech.GRAttestation.sections.tables

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.css.*
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.icons.fa.FaDownload
import com.varabyte.kobweb.silk.components.icons.fa.FaEllipsisVertical
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.toModifier
import gr.tech.GRAttestation.models.attestation.AttestationDTO
import gr.tech.GRAttestation.sections.admin.AdminTableContainer
import gr.tech.GRAttestation.styles.admin.AdminTableStickyColumn
import gr.tech.GRAttestation.styles.grstyles.DropdownLinkStyle
import gr.tech.GRAttestation.util.ROUTES
import gr.tech.GRAttestation.util.TimeUtils.formatDateOnly
import org.jetbrains.compose.web.css.cssRem
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.*

@Composable
fun AdminStoreAttestationsTable(
    attestations: List<AttestationDTO>,
    onGenReceipts: (AttestationDTO) -> Unit,
    onDelete: (AttestationDTO) -> Unit,
    onPreview: (AttestationDTO) -> Unit,
) {
    AdminTableContainer() {
        Table(
            attrs = Modifier
                .classNames("table")
                .minWidth(1000.px)
                .height(100.percent)
                .fillMaxWidth()
                .toAttrs()
        ) {
            Thead(
                attrs = Modifier
                    .styleModifier {
                        property("table-layout", "fixed")
                    }
                    .toAttrs()
            ) {
                Tr {
                    listOf(
                        "ID",
                        "PDF",
                        "Sales Data",
                        "Rep Name",
                        "Sign Date",
                        "Created At",
                    ).forEach { header ->
                        Th(
                            attrs = Modifier.minWidth(100.px).classNames("col").toAttrs()
                        ) { Text(header) }
                    }
                    Th(
                        attrs = Modifier
                            .then(AdminTableStickyColumn.toModifier())
                            .width(175.px)
                            .toAttrs()
                    ) {
                        Text("Action")
                    }
                }
            }
            Tbody {
                if (attestations.isNotEmpty()) {
                    attestations.forEach { form ->
                        AdminAttestationTableRow(
                            attestation = form,
                            onGenReceipts = { onGenReceipts(it) },
                            onDelete = { onDelete(it) },
                            onPreview = { onPreview(it) }
                        )
                    }
                } else {
                    repeat(2) {
                        AdminActivitySummaryRow(null)
                    }
                }
            }
        }
    }
}

@Composable
fun AdminAttestationTableRow(
    attestation: AttestationDTO,
    onGenReceipts: (AttestationDTO) -> Unit,
    onDelete: (AttestationDTO) -> Unit,
    onPreview: (AttestationDTO) -> Unit,
) {
    Tr {
        Td { Text(attestation.id.toString())}
        Td(
            attrs = Modifier.maxWidth(250.px).toAttrs()
        ) {
            Span(
                attrs = Modifier
                    .color(Colors.Blue)
                    .textDecorationLine(TextDecorationLine.Underline)
                    .cursor(Cursor.Pointer)
                    .padding(0.px)
                    .onClick { onPreview(attestation) }
                    .toAttrs()
            ) {
                Text("Preview PDF")
            }
        }
        Td(
            attrs = Modifier.minWidth(350.px)
                .overflow(Overflow.Scroll).textOverflow(TextOverflow.Ellipsis).whiteSpace(WhiteSpace.NoWrap).toAttrs()
        )  {
            attestation.salesData.forEach { sale ->
                P(attrs = Modifier.margin(bottom = 4.px).toAttrs())
                {Text("x${sale.quantity} ${sale.skuModel.sku} | ${sale.skuModel.description} | ${sale.dateOfSale}") }
            }
        }
        Td { Text(attestation.repName) }
        Td {
            Span { Text(formatDateOnly(attestation.dateSigned)) }
        }
        Td {
            Span { Text(formatDateOnly(attestation.createdAt)) }
            TableDateTimeTooltip(attestation.createdAt)
        }

        // Action column
        Td(
            attrs = Modifier
                .padding(.5.cssRem)
                .then(AdminTableStickyColumn.toModifier())
                .toAttrs()
        ) {
            Div(
                attrs = Modifier
                    .classNames("btn-group")
                    .toAttrs()
            ) {
                Div(
                    attrs = Modifier
                        .classNames("btn-group", "dropstart")
                        .toAttrs()
                ) {
                    Button(
                        attrs = Modifier
                            .classNames("btn", "btn-outline-secondary")
                            .toAttrs{
                                attr("data-bs-toggle", "dropdown")
                            }
                    ) {
                        FaEllipsisVertical()
                    }
                    Ul(
                        attrs = Modifier.classNames("dropdown-menu")
                            .toAttrs()
                    ) {
                        Li {
                            Span(
                                attrs = DropdownLinkStyle.toModifier().classNames("dropdown-item")
                                    .onClick { onGenReceipts(attestation) }
                                    .toAttrs()
                            ) {
                                Text("Generate Receipts")
                            }
                        }
                        Li {
                            Span(
                                attrs = DropdownLinkStyle.toModifier().classNames("dropdown-item")
                                    .onClick { onDelete(attestation) }
                                    .toAttrs()
                            ) {
                                Text("Delete")
                            }
                        }
                    }
                }

                A(
                    href = "${ROUTES.BACKEND.FORMS.ADMIN_FORM_DOWNLOAD}/${attestation.id}",
                    attrs = DownloadButtonStyle.toModifier().classNames("btn", "btn-outline-secondary").fillMaxSize().toAttrs {
                        attr("title", "Download PDF")
                    }
                ) {
                    FaDownload()
                }
            }
        }
    }
}

val DownloadButtonStyle = CssStyle {
    cssRule("> a") {
        Modifier
            .color(Colors.Grey)
    }
    cssRule(":hover > a") {
        Modifier
            .color(Colors.White)
    }
}